import { PositioningMethod } from "building-types/annotation/annotationTypesEnum";
import type { Building } from "building-types/building/buildingTypes";
import type { GeneratedMeasurement } from "building-types/entities/measurementTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { Table } from "common/data/table";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { generateMeasurement, mirrorMeasurement } from "../elements/measurement/measurement";
import { measurementTransformToGlobal } from "../generationTools/generateMeasurementsTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  doc: { measurements, optionSets, instanceOptionSets },
  mirror,
  mirrorTranslationX,
  rooms,
  optionChoices,
  entityDisabledByGroup,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    measurements,
    optionChoices: resolveOptionsInFormula(measurements, definitions, optionChoices),
    entityDisabledByGroup,
    rooms,
    mirror,
    mirrorTranslationX,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  measurements: "deep",
});

function generate({
  measurements,
  rooms,
  optionChoices,
  mirror,
  mirrorTranslationX,
  entityDisabledByGroup,
}: ReturnType<typeof select>): {
  measurements: Table<GeneratedMeasurement>;
  issues: BuildingGenerationIssue[];
} {
  const resultMeasurements: { [id: string]: GeneratedMeasurement } = {};
  const issues: BuildingGenerationIssue[] = [];

  measurements = evalFormulas(measurements, (v) => optionChoices[v], undefined, entityDisabledByGroup);

  for (const id in measurements) {
    const measurement = measurements[id];
    if (measurement.disabled) {
      continue;
    }

    const room = rooms[measurement.parentId];
    if (!room) {
      // room might be disabled
      continue;
    }

    let mirroredMeasurement = mirrorMeasurement({
      measurement,
      mirror,
      mirrorTranslationX: measurement.positioningMethod === PositioningMethod.EDGE ? 0 : mirrorTranslationX,
    });

    if (measurement.positioningMethod === PositioningMethod.EDGE) {
      mirroredMeasurement = measurementTransformToGlobal(mirroredMeasurement, room, mirror, issues);
    }

    resultMeasurements[id] = generateMeasurement(mirroredMeasurement);
  }

  return { measurements: resultMeasurements, issues };
}

export default memo(select, generate, compare);
